import React from 'react'
import { HeadingText, BodyText, CaptionText } from '@paypalcorp/pp-react'
import Tags from '../Tags'
import { Card } from '../Card'
import useBreakpoint from '../../../../../lib/useBreakpoint'
import { OfferCardType } from '../../../types/ShoppingPageType'
import OfferCardButton from './OfferCardButton'
import {
  handleButtonOfferOutclick,
  handleCardOfferOutclick
} from '../../../../../lib/handleOfferOutclick'
import { StoreProfileFPTI } from '../../../../store-profile/StoreProfilePage'
import useWorldReady from 'hooks/useWorldReady'

interface OfferCardMetaProps {
  isMobile: boolean
  offerMeta: string
}

interface OfferCardTitleProps {
  isMobile: boolean
  offerMeta: string
  offerTypeBadge: string
}

interface OfferCardProps {
  offerData: OfferCardType
  landingUrl?: string
  fptiData: StoreProfileFPTI | null
}

const OfferCardMeta = ({ isMobile, offerMeta }: OfferCardMetaProps) => {
  if (isMobile) {
    return <BodyText>{offerMeta}</BodyText>
  }

  return <HeadingText size="sm">{offerMeta}</HeadingText>
}

const OfferCardTitle = ({
  isMobile,
  offerMeta,
  offerTypeBadge
}: OfferCardTitleProps): JSX.Element => {
  return (
    <>
      <OfferCardMeta isMobile={isMobile} offerMeta={offerMeta} />
      <CaptionText className="offer-card--v2__badge ppvx_text--caption ppvx--v2">
        {offerTypeBadge.toLowerCase()}
      </CaptionText>
    </>
  )
}

const OfferCard = ({
  offerData,
  fptiData,
  landingUrl
}: OfferCardProps): JSX.Element | null => {
  const { formatMessage } = useWorldReady()
  const { isMobile } = useBreakpoint()

  if (!offerData) {
    return null
  }

  const { description, subtitle, honeyOfferType, tags, customRedemptionCode } =
    offerData

  const clipboardText = customRedemptionCode || ''
  const isHoneyCode = honeyOfferType === 'CODE'
  const buttonText = isHoneyCode
    ? formatMessage('main.storePage.offerDetails.copyCodeText')
    : formatMessage('main.storePage.offerDetails.shopNow')

  const handleCardClick = () => {
    handleCardOfferOutclick(offerData, fptiData, landingUrl)
  }

  const handleButtonClick = () => {
    handleButtonOfferOutclick(offerData, fptiData, landingUrl)
  }

  return (
    <Card onClick={handleCardClick}>
      <div className="offer-card--v2" data-cy="offer-card--v2">
        <div
          className="offer-card--v2__side-box"
          data-cy="offer-card--v2__side-box"
        >
          <OfferCardTitle
            isMobile={isMobile}
            offerMeta={subtitle}
            offerTypeBadge={honeyOfferType}
          />
        </div>
        <div
          className="offer-card--v2__divider"
          data-cy="offer-card--v2__divider"
        />
        <div
          className="offer-card--v2__content-box"
          data-cy="offer-card--v2__content-box"
        >
          <div
            className="offer-card--v2__details"
            data-cy="offer-card--v2__details"
          >
            <BodyText className="offer-card--v2__title">{description}</BodyText>
            <div className="offer-card--v2__tags">
              <Tags tags={tags} />
            </div>
            <OfferCardButton
              className="offer-card--v2__mobile-button"
              isVisible={isMobile}
              isMobile={isMobile}
              buttonText={buttonText}
              isCheckButton={isHoneyCode}
              clipboardText={clipboardText}
              onClick={handleButtonClick}
            />
          </div>
          <OfferCardButton
            className="offer-card--v2__button"
            isVisible={!isMobile}
            isMobile={isMobile}
            buttonText={buttonText}
            isCheckButton={isHoneyCode}
            clipboardText={clipboardText}
            onClick={handleButtonClick}
          />
        </div>
      </div>
    </Card>
  )
}

export default OfferCard
